/* eslint-disable */
import React, { useState, useEffect, useRef } from "react"
import { graphql } from "gatsby"
import { Meta, Eyebrow, Headline, GridLines, ContentCopy } from "@shared"
import { CONSTANTS, URLS } from "@data"

import { logTool, css } from "@jsUtils"

import Styles from "./styles/technology.module.scss"
import Blob from "@assets/images/blob.png"
import Chip from "@assets/images/chip.png"

import { Footer, Header, Subscribe, EmailModal } from "@components/sections"
import {
  TechnologyLanding,
  TechnologyScrollSection,
  TechnologySection
} from "@components/technologySections"
import { section1 } from "@data/sequences/section1"
import { section3 } from "@data/sequences/section3"

let THREE, OBJLoader, scene, camera, renderer
let heart = null
let rotation = 0

const { PAGE_NAMES, THEMES, COLORS } = CONSTANTS
let _scroll = 0,
  animation,
  i = 0

const log = logTool(true)

/**
 * Technology Page
 * @param {object} props - react props
 */
const IndexPage = props => {
  const { data } = props
  const { contentfulPageMeta, contentfulTechnologyPage, contentfulCrmEventBasedModal } = data
  const { bottomBanner, productName } = contentfulTechnologyPage
  const { isModalEnabledOnSite } = contentfulCrmEventBasedModal
  const [mounted, setMounted] = useState(false)
  const [currentScroll, setCurrentScroll] = useState(-1)
  const [currentPercent, setCurrentPercent] = useState(-1)
  const [pageHeight, setPageHeight] = useState(-1)
  const [direction, setDirection] = useState("")
  const [sticky, setSticky] = useState(false)
  const [sequences, setSequences] = useState([])
  const [sections, setSections] = useState([])
  const [currentSection, setCurrentSection] = useState(0)
  const [nextSection, setNextSection] = useState(1)
  const [prevSection, setPrevSection] = useState(-1)
  const [sectionHeight, setSectionsHeight] = useState(-1)
  const [loaded, setLoaded] = useState(false)
  const pageRef = useRef()
  const heartRender = useRef()

  // log("Technology page content", { contentfulPageMeta })

  const animate = () => {
    cancelAnimationFrame(animation)
    animation = requestAnimationFrame(animate)
    // cache the scroll and set
    _scroll = window.scrollY

    // Defer every other frame
    // This helps perf in a big way - the set state call tends to be noisy
    // By only triggering every other frame we can ensure that we don't overload calls
    // and frames can actually render
    if (i % 2) {
      // // Generate percentage based on scroll
      if (_scroll <= sectionHeight) {
        if (!sticky) setSticky(true)
      } else {
        if (sticky) setSticky(false)
      }

      if (heart) {
        rotation -= 0.01
        heart.rotation.set(0, rotation, 0)
        renderer.render(scene, camera)
      }

      if (_scroll != currentScroll) setCurrentScroll(_scroll)
    } else {
      // Set direction
      let _direction = ""
      if (window.scrollY < currentScroll) {
        _direction = "up"
      }
      if (window.scrollY > currentScroll) {
        _direction = "down"
      }
      if (_direction != direction) setDirection(_direction)

      if (sections.length > 0) {
        sections.map((section, index) => {
          let _sectionScroll = section.bounds.bottom - _scroll
          let _scrollTop = section.bounds.top - _scroll

          // TEMP - Adjust to deal with up / down
          if (_scrollTop < window.innerHeight / 2 && _sectionScroll > section.bounds.height / 2) {
            if (currentSection != index) {
              setCurrentSection(index)
            }
          }
        })
      }
    }
    i++
  }

  const preload = section => {
    return new Promise((resolve, reject) => {
      let _load = []
      section.map((asset, index) => {
        let assetLoad = new Promise((resolve, reject) => {
          let img = new Image()
          img.onload = data => {
            asset.asset = data.target
            resolve()
          }
          img.src = asset.path
        })
        _load.push(assetLoad)
      })

      Promise.all(_load).then(() => {
        resolve()
      })
    })
  }

  const register = (ref, type, asset, start, loop = false, out) => {
    let _section = {
      ref: ref.current,
      asset: asset,
      type: type,
      start: start,
      index: sections.length,
      loop: loop,
      out: out,
      bounds: {
        height: ref.current.getBoundingClientRect().height,
        top: ref.current.offsetTop,
        bottom: ref.current.offsetTop + ref.current.getBoundingClientRect().height
      }
    }
    sections.push(_section)
    const result = []
    const map = new Map()
    for (const item of sections) {
      if (!map.has(item.id)) {
        map.set(item.id, true)
        result.push(item)
      }
    }

    setSections([...sections])
  }

  const createScene = () => {
    let width = heartRender.current.getBoundingClientRect().width
    let height = heartRender.current.getBoundingClientRect().height
    camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 1000)
    camera.position.set(0, 0, 35)
    renderer.setSize(width, height)
    heartRender.current.appendChild(renderer.domElement)

    let loader = new OBJLoader()
    loader.load(
      "/models/heart-model.obj",
      function(object) {
        heart = object
        heart.children[0].material.color = new THREE.Color(0xff0000)
        heart.children[0].material.size = 0.17
        heart.children[0].material.sizeAttenuation = true
        heart.position.set(0, -3, 0)
        scene.add(heart)
        animate()
      },
      // called when loading is in progresses
      function(xhr) {
        log((xhr.loaded / xhr.total) * 100 + "% loaded")
      },
      // called when loading has errors
      function(error) {
        console.error("An error happened", error)
      }
    )
  }

  const resize = () => {
    let _newHeight = 0
    sections.map(section => {
      section.bounds.height = section.ref.getBoundingClientRect().height
      section.bounds.width = section.ref.getBoundingClientRect().width
      section.bounds.bottom = section.ref.offsetTop + section.ref.getBoundingClientRect().height
      _newHeight += section.bounds.height
    })

    let width = heartRender.current.getBoundingClientRect().width
    let height = heartRender.current.getBoundingClientRect().height
    camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 1000)
    camera.position.set(0, 0, 35)
    renderer.setSize(width, height)

    setSectionsHeight(_newHeight)
  }

  // useEffect(() => {
  //   if (pageHeight === 0) return
  //   animate()
  // }, [pageHeight])

  useEffect(() => {
    let _newHeight = 0
    sections.map(section => {
      _newHeight += section.bounds.height
    })
    setSectionsHeight(_newHeight)
  }, [sections])

  useEffect(() => {
    animate()
  }, [sectionHeight, sticky])

  useEffect(() => {
    let _currentSection = currentSection
    setNextSection(_currentSection++)
    setPrevSection(_currentSection--)
    animate()
  }, [currentSection])

  useEffect(() => {
    let timeout
    const onResize = () => {
      if (timeout) clearTimeout(timeout)
      timeout = setTimeout(() => {
        resize()
        clearTimeout(timeout)
      }, 50)
    }

    if (!mounted) {
      let app = document.getElementById("___gatsby").getBoundingClientRect()
      setPageHeight(
        pageRef.current.getBoundingClientRect().height -
          (app.height - pageRef.current.getBoundingClientRect().bottom)
      )

      preload(section1).then(() => {
        sequences.push(section1)
        setSequences([...sequences])

        preload(section3).then(() => {
          sequences.push(section3)
          setSequences([...sequences])
        })
      })

      if (window.document) {
        THREE = require("three")
        OBJLoader = require("three/examples/jsm/loaders/OBJLoader.js").OBJLoader
        scene = new THREE.Scene()
        renderer = new THREE.WebGLRenderer()
        createScene()
      }

      window.addEventListener("resize", onResize)

      setMounted(true)
    }
    return () => {
      window.removeEventListener("resize", onResize)
    }
  }, [mounted])

  return (
    <>
      <Meta base={contentfulPageMeta} page={contentfulTechnologyPage} />
      <Header theme={THEMES.BLACK} pageColor={COLORS.DARK} />
      <div ref={pageRef} className={css(Styles.component)}>
        <TechnologySection register={register} type="sequence" asset={0} start={50}>
          <GridLines theme={THEMES.DARK} />
          <TechnologyLanding />
        </TechnologySection>
        <TechnologySection register={register} type="video" asset="/videos/chapter-2.mp4">
          <GridLines theme={THEMES.DARK} />
          <div className={Styles.section2Content}>
            <Eyebrow color="fluro">Silicon Meets Piezo</Eyebrow>
            <Headline className={Styles.firstHeadline} size={1}>
              Ultra-wideband
              <br />
              Ultrasound
            </Headline>
            <Headline className={Styles.headline} size={4}>
              Our patented ultrasound technology combines advances in nanotechnology with
              cutting-edge chip design to enable an unrivaled instrument. Engineered at an atomic
              scale, our Piezoelectric Micromachined Ultrasonic Transducer (pMUT) based technology
              delivers an ultra-wideband imaging transducer at low voltage with unprecedented
              performance and acoustic efficiency.
            </Headline>
          </div>
        </TechnologySection>
        <TechnologySection
          register={register}
          type="video"
          asset="/videos/chapter-2-transition.mp4"
        >
          <GridLines theme={THEMES.DARK} />
        </TechnologySection>
        <TechnologySection register={register} type="sequence" asset={1} start={-100}>
          <GridLines theme={THEMES.DARK} />
          <div className={Styles.section3Content}>
            <Headline className={Styles.headline} size={4}>
              Our acoustic waveguide is capable of creating and receiving deep bass frequencies and
              sublime high notes needed for stellar imaging.
            </Headline>
          </div>
        </TechnologySection>
        <TechnologySection register={register} type="video" asset="/videos/chapter-5.mp4">
          <GridLines theme={THEMES.DARK} />
          <div className={Styles.section4Content}>
            <Eyebrow color="fluro">An Orchestra on a Chip</Eyebrow>
            <Headline className={Styles.firstHeadline} size={1}>
              From Symphony
              <br />
              to Epiphany
            </Headline>
            <Headline className={Styles.headline} size={5}>
              Our revolutionary ultrasound technology features an advanced silicon chip with a Full
              Matrix of 4096 patented pMUT transducers that are conducted like an orchestra.
            </Headline>
          </div>
        </TechnologySection>
        <TechnologySection
          register={register}
          type="video"
          asset="/videos/chapter-6.mp4"
          loop={true}
          out="fade"
        >
          <GridLines theme={THEMES.DARK} />
          <div className={Styles.section5Content}>
            <Headline size={3}>
              Every pMUT is individually synchronized to the nanosecond to create an ultrasonic
              concert like no other.
            </Headline>
          </div>
        </TechnologySection>
        <div className={css(Styles.techSection, Styles.first)}>
          <div className={css(Styles.section6Content)}>
            <Eyebrow className={Styles.eyebrow} color="fluro">
              Sonography to Tomography
            </Eyebrow>
            <Headline className={Styles.firstHeadline} size={1}>
              Diagnose in
              <br />
              three dimensions
            </Headline>
            <Headline className={Styles.headline} size={5}>
              On-chip 3D acoustic beam-steering and focusing, micro-beam formation, combined with
              high-speed compute, allow our first-of-its kind ultrasound technology to obtain and
              dice 3D volumetric images. That way, you get the full picture any way you slice it.
            </Headline>
            <div className={Styles.heartRender} ref={heartRender}></div>
          </div>
        </div>
        <div className={css(Styles.techSection)}>
          <div className={css(Styles.section7Content)}>
            {/* <Headline size={2}>Ultra-High Dynamic Range</Headline> */}
            <div className={Styles.blob}>
              <div
                className={Styles.blobWrapper}
                style={{
                  backgroundImage: `url(${Blob})`
                }}
              ></div>
              <div className={Styles.featureCallout}>
                <Headline className={Styles.oneIn} size={3}>
                  Multi- Harmonic and Ultra-High Dynamic Range
                </Headline>
                {/* <Headline className={Styles.headline} size={5}>
                  Each of the 4096 pMUTs on {productName}™ features individual transmit and
                  ultra-low noise receive electronics allowing an industry leading dynamic range.
                </Headline> */}
              </div>
            </div>
            <Headline className={Styles.featureCalloutB} size={4}>
              Create intense sound pressure levels required for multi-harmonic imaging and
              therapeutics while its ultra-low noise amplifiers can record the faintest incoming
              echoes to create an industry leading dynamic range.
            </Headline>
          </div>
        </div>
        <div className={css(Styles.techSection, Styles.last)}>
          <div className={css(Styles.section8Content)}>
            <Headline size={2}>Make Music Together</Headline>
            <div className={Styles.productInfo}>
              <div className={Styles.column}>
                <Headline className={Styles.headline} size={4}>
                  We believe technology this powerful should be available for others to help create
                  the future.
                </Headline>
                <div className={Styles.copy}>
                  That’s why we partner with leading companies to leverage our silicon technologies
                  across a broad range of diagnostic and therapeutic applications – from tomography
                  machines to wearable devices and much more.
                </div>
                <div className={Styles.copy}>Potential applications:</div>
                <div className={Styles.features}>
                  <img
                    src={Chip}
                    className={css(Styles.mobile)}
                    alt="pMUT, Piezoelectric Micromachined Ultrasonic Transducer, ultrasound chip"
                    width="100%"
                  />
                  <ul className={Styles.techText}>
                    <li>Patches</li>
                    <li>Tomography</li>
                    <li>Catheters</li>
                    <li>Neural Communications</li>
                    <li>Acousticeuticals</li>
                    <li>HIFU</li>
                  </ul>
                </div>
              </div>
              <div className={css(Styles.column, Styles.desktop)}>
                <img src={Chip} />
              </div>
            </div>
          </div>
        </div>
        <TechnologyScrollSection
          currentScroll={currentScroll}
          currentPercent={currentPercent}
          sequences={sequences}
          direction={direction}
          sections={sections}
          currentSection={currentSection}
          nextSection={nextSection}
          prevSection={prevSection}
          sticky={sticky}
        ></TechnologyScrollSection>
      </div>

      <Subscribe theme={THEMES.DARK} color={COLORS.FLURO} />
      {/* {isModalEnabledOnSite ? (
        <EmailModal data={contentfulCrmEventBasedModal} theme={THEMES.DARK} color={COLORS.FLURO} />
      ) : null} */}
      <Footer />
    </>
  )
}

/**
 * pageQuery for Home page
 * @todo is there a way to make the global data a fragment?
 */
export const pageQuery = graphql`
  {
    contentfulPageMeta(id: { eq: "423babd8-0278-5b2e-85f6-77fb7ee55bee" }) {
      ...GlobalMetaData
    }
    contentfulCrmEventBasedModal {
      heroImage {
        title
        fluid(quality: 100, maxWidth: 1920) {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      supportingText
      heroText
      isModalEnabledOnSite
    }
    contentfulTechnologyPage {
      id
      title
      pageMeta {
        ...PageMetaData
      }
      productName
      bottomBanner {
        ...BannerSection
      }
    }
  }
`

export default IndexPage
